import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    IMotivationalNudgeImage,
    IOfferingContact,
    IOfferingContactRequest,
    IOfferingContactRequestBase,
    IPartnerAccountingTotals,
    IPartnerLedgerItem,
    IPartnerUser,
    IPayoutDetail,
    ISendMotivationalNudgeRequest,
    ISendMotivationalNudgeResult,
    ITotals,
    IUserSearchResult,
} from 'app/administration/models';
import { IApprovePayout } from 'app/administration/models/approve-payout';
import { IPartnerBase, IPartnerDeposit } from 'app/administration/models/partner-base';
import { IPartnerTheme, IPartnerThemeAsset, IPartnerThemeView, PartnerThemeAssetType } from 'app/administration/models/theme/partner-theme';
import { IOffering } from 'app/models';
import { AppConfigService } from 'app/shared/services/app-config.service';
import { ApiService } from 'app/shared/services/base/api.service';
import { IBaseApiResponse } from 'app/shared/services/base/base-api.service';
import { createFormData } from 'app/shared/utilities/api-utilities';

import { catchError, map, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AdministrationApiService extends ApiService {
    constructor(http: HttpClient, appConfig: AppConfigService) {
        super(http, appConfig);
    }

    getPaymentQueue(): Observable<IPayoutDetail[]> {
        const route: string = `administration/payouts`;
        return this.getArray$<IPayoutDetail>(route);
    }

    getTotals(): Observable<ITotals> {
        const route: string = `administration/totals`;
        return this.getObject$<ITotals>(route);
    }

    setIntroductionMeeting(status: IApprovePayout): Observable<IBaseApiResponse<{}>> {
        const route: string = `administration/payout/approve`;
        return this.post<IApprovePayout>(route, status) as Observable<IBaseApiResponse<{}>>;
    }

    getPayoutDocument(payoutUuid: string, storageName: string): Observable<string> {
        const route: string = `administration/payout/${payoutUuid}/document/${storageName}`;
        return this.get$(route);
    }

    getPatners(includeBeacon?: boolean): Observable<IPartnerBase[]> {
        let params: HttpParams = new HttpParams();
        if (includeBeacon) {
            params = params.append('includeBeacon', `${true}`);
        }
        const route: string = `administration/partners`;
        return this.getArray$<IPartnerBase>(route, params);
    }

    partnerDeposit(deposit: IPartnerDeposit): Observable<IBaseApiResponse<{}>> {
        const route: string = `administration/accounting/deposit`;
        return this.post<IPartnerDeposit>(route, deposit) as Observable<IBaseApiResponse<{}>>;
    }

    getPartnerAccountingTotals(partnerUuid: string): Observable<IPartnerAccountingTotals> {
        const route: string = `administration/partner/${partnerUuid}/totals`;
        return this.getObject$<IPartnerAccountingTotals>(route);
    }

    getPartnerAccountingItems(partnerUuid: string): Observable<IPartnerLedgerItem[]> {
        const route: string = `administration/partner/${partnerUuid}/ledger`;
        return this.getArray$<IPartnerLedgerItem>(route);
    }

    getTheme(partnerUuid: string): Observable<IPartnerThemeView> {
        const route: string = `administration/partner/${partnerUuid}/theme`;
        return this.getObject$<IPartnerThemeView>(route);
    }

    saveTheme(partnerUuid: string, payload: IPartnerTheme): Observable<IBaseApiResponse<IPartnerThemeView>> {
        const route: string = `administration/partner/${partnerUuid}/theme`;
        return this.postWithReturnObject<IPartnerTheme, IPartnerThemeView>(route, payload) as Observable<IBaseApiResponse<IPartnerThemeView>>;
    }

    uploadAsset(partnerUuid: string, assetType: PartnerThemeAssetType, file: File): Observable<IPartnerThemeAsset> {
        const route: string = `administration/partner/${partnerUuid}/theme/assets/${assetType}`;
        const formData = createFormData({
            file,
        });
        return this.http.post<{ fileName: string; url: string }>(this.url(route), formData).pipe(
            map((body: IPartnerThemeAsset) => {
                return body;
            }),
            catchError((e: HttpErrorResponse) => {
                return this.handleTypedError<IPartnerThemeAsset>(e, this.appInsights, false);
            })
        );
    }

    removeAsset(partnerUuid: string, assetType: PartnerThemeAssetType): Observable<boolean> {
        const route: string = `administration/partner/${partnerUuid}/theme/assets/${assetType}`;
        return this.delete$(route);
    }

    getOfferings(companyId: number): Observable<IOffering[]> {
        let params: HttpParams = new HttpParams();
        params = params.append('includeInactive', `${true}`);
        const route: string = `Company/${companyId}/Offerings`;
        return this.getArray$<IOffering>(route, params);
    }

    getOffering(offeringId: number): Observable<IOffering> {
        const route: string = `administration/offerings/${offeringId}`;
        return this.getObject$<IOffering>(route);
    }

    createOffering(offering: IOffering): Observable<IBaseApiResponse<IOffering>> {
        const route: string = `administration/offerings`;
        return this.postWithReturnObject<IOffering, IOffering>(route, offering) as Observable<IBaseApiResponse<IOffering>>;
    }

    updateOffering(model: IOffering): Observable<IBaseApiResponse<IOffering>> {
        const route: string = `administration/offerings/${model.id}`;
        return this.postWithReturnObject<IOffering, IOffering>(route, model) as Observable<IBaseApiResponse<IOffering>>;
    }

    deleteOffering(offeringId: number): Observable<boolean> {
        const route: string = `administration/offerings/${offeringId}`;
        return this.delete$(route);
    }

    getCompanyUsers(partnerId: number): Observable<IPartnerUser[]> {
        const route: string = `administration/partner/${partnerId}/users`;
        return this.getArray$<IPartnerUser>(route);
    }

    addPartnerUser(partnerId: number, userId: number): Observable<IBaseApiResponse<{}>> {
        const route: string = `administration/partner/${partnerId}/users`;
        return this.post<{ userId: number }>(route, { userId }) as Observable<IBaseApiResponse<{}>>;
    }

    deleteParnterUser(partnerId: number, userId: number): Observable<boolean> {
        const route: string = `administration/partner/${partnerId}/users/${userId}`;
        return this.delete$(route);
    }

    searchPlatformUsers(query: string, partnerId: number): Observable<IUserSearchResult[]> {
        let params: HttpParams = new HttpParams();
        params = params.append('q', `${query}`);
        params = params.append('partnerId', `${partnerId}`);
        const route: string = `administration/users/search`;
        return this.getArray$<IUserSearchResult>(route, params);
    }

    getOfferingContacts(offeringId: number): Observable<IOfferingContact[]> {
        const route: string = `administration/offerings/${offeringId}/contacts`;
        return this.getArray$<IOfferingContact>(route);
    }

    addOfferingContact(offeringId: number, contact: IOfferingContactRequest): Observable<IBaseApiResponse<{}>> {
        const route: string = `administration/offerings/${offeringId}/contacts`;
        return this.post<IOfferingContactRequest>(route, contact) as Observable<IBaseApiResponse<{}>>;
    }

    updateOfferingContact(offeringId: number, userId: number, contact: IOfferingContactRequestBase): Observable<boolean> {
        const route: string = `administration/offerings/${offeringId}/contacts/${userId}`;
        return this.patch$(route, contact, false);
    }

    deleteOfferingContact(offeringId: number, userId: number): Observable<boolean> {
        const route: string = `administration/offerings/${offeringId}/contacts/${userId}`;
        return this.delete$(route);
    }

    sendMotivationalNudge(request: ISendMotivationalNudgeRequest): Observable<IBaseApiResponse<ISendMotivationalNudgeResult>> {
        const route: string = `administration/nudges/send`;
        return this.postWithReturnObject<ISendMotivationalNudgeRequest, ISendMotivationalNudgeResult>(route, request) as Observable<IBaseApiResponse<ISendMotivationalNudgeResult>>;
    }

    getMotivationalNudgeImages(): Observable<IMotivationalNudgeImage[]> {
        const route: string = `administration/nudges/images`;
        return this.getArray$<IMotivationalNudgeImage>(route);
    }
}
